import Vue from "vue";
import i18n, { supportedLanguages, defaultLanguage } from "@/plugins/i18n";
import { GetterTree, ActionTree, MutationTree } from "vuex";
import { LanguageView } from "@/shared/models/languages";
import { ListModel } from "@/shared/models/listModel";

const savedDataString = localStorage.getItem('app.language');
const savedData = savedDataString
  ? JSON.parse(savedDataString) as { language: string | null; textResource: ({ [key: string]: string } | null) }
  : { language: i18n.locale, textResource: {} as { [key: string]: string } };

const locale = savedData.language
  || navigator.languages.find(x => supportedLanguages.some(y => y === x))
  || i18n.locale;
i18n.setLocaleMessage(locale, savedData.textResource || {});
i18n.locale = locale;

const state = {
  inited: false,
  languages: [] as LanguageView[]
};

const getters: GetterTree<typeof state, any> = {
  inited: (s) => s.inited,
  language: () => i18n.locale,
  supportedLanguages: () => {
    return (state.languages?.map(e => {
      const supportedLanguage = supportedLanguages.find(sp => sp.split('-')[0] === e.languageCode)
      return {
        ...e,
        cultureCode: supportedLanguage ?? ""
      } as LanguageView
    }) ?? []).filter(e => e.cultureCode)
  }
};

const actions: ActionTree<typeof state, any> = {
  async getTextResource({ dispatch }): Promise<{ [key: string]: string }> {
    const tr = Object.keys(i18n.getLocaleMessage(i18n.locale));
    return tr.length > 0
      ? Promise.resolve(tr)
      : await dispatch('setLanguage', i18n.fallbackLocale);
  },
  async setLanguage({ commit }, language: string | null): Promise<{ [key: string]: string }> {
    if (language) {
      return Vue.axios
        .get<{ [key: string]: string }>(`system/GetTextResource?culture=${language}`)
        .then(x => {
          commit('setLanguage', { language, textResource: x.data });
          return x.data;
        })
    } else {
      commit('setLanguage', { language: null, textResource: null });
      return Promise.resolve({});
    }
  },
  refreshLanguages({ commit }) {
    Vue.axios.get<ListModel<LanguageView>>("codebooks/languages").then((res) => {
      commit("setLanguages", res.data.result);
    });
  }
};
const mutations: MutationTree<any> = {
  setLanguage: (state, payload: { language: string | null; textResource: { [key: string]: string } | null }) => {
    if (payload.language) {
      localStorage.setItem('app.language', JSON.stringify(payload));
      document.cookie = `.AspNetCore.Culture=${payload.language}; path=/`
      i18n.mergeLocaleMessage(payload.language, payload.textResource || {});
      i18n.locale = payload.language;
    } else {
      localStorage.removeItem('app.language');
      document.cookie = `.AspNetCore.Culture=${defaultLanguage}; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/`
    }
    state.inited = true;
  },
  setLanguages: (state, value: LanguageView[]) => {
    state.languages = value.slice();
  }
};

export default {
  namespaced: true,
  state: () => state,
  getters,
  actions,
  mutations
};
